import { FEATURE_FLAGS } from "@repo/shared";
import type { CostOverrideMode, LocalizedString } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LocalizedInput } from "@/components/ui/localized-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useClientCostCategories } from "@/lib/hooks/use-client-cost-categories";
import { useFeatureFlag } from "@/lib/hooks/use-feature-flags";
import {
  useCreatePortfolioCost,
  useProductCostClasses,
  useUpdatePortfolioCost,
} from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { AdvisorOverrideSelect } from "./advisor-override-select";
import { T } from "./manual-fee-dialog.i18n";

type Category = "RECURRING" | "DEPOSIT" | "WITHDRAWAL";

type ManualFeeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  portfolioCollectionId: string;
  manualFee: any | null;
};

export function ManualFeeDialog({
  open,
  onOpenChange,
  portfolioCollectionId,
  manualFee,
}: ManualFeeDialogProps) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  const createPCost = useCreatePortfolioCost();
  const updatePCost = useUpdatePortfolioCost();
  const isPending = createPCost.isPending || updatePCost.isPending;

  const { clientCostCategories } = useClientCostCategories();
  const { productCostClasses } = useProductCostClasses(portfolioCollectionId);
  const activeClientCategories = clientCostCategories.filter((c) => c.isActive);
  const activeProductClasses = productCostClasses.filter((c) => c.isActive);

  const [name, setName] = useState("");
  const [nameI18n, setNameI18n] = useState<LocalizedString>({});
  const [category, setCategory] = useState<Category>("RECURRING");
  const [valuePct, setValuePct] = useState("");
  const [minFee, setMinFee] = useState("");
  const [clientCostCategoryId, setClientCostCategoryId] = useState("");
  const [productCostClassId, setProductCostClassId] = useState("");
  const [error, setError] = useState<string | null>(null);
  // QL-20 — shown and sent only while the tenant's cost_overrides feature is on.
  const { enabled: overridesEnabled } = useFeatureFlag(FEATURE_FLAGS.COST_OVERRIDES);
  const [advisorOverride, setAdvisorOverride] = useState<CostOverrideMode>("off");

  useEffect(() => {
    if (open) setAdvisorOverride(manualFee?.advisorOverride ?? "off");
  }, [open, manualFee]);

  const isEditing = manualFee !== null;
  const editingManualFeeName = manualFee ? localize(manualFee.nameI18n) : "";
  // A minimum fee only makes sense for per-event (deposit/withdrawal) fees.
  const supportsMinFee = category === "DEPOSIT" || category === "WITHDRAWAL";

  const CATEGORY_LABELS: Record<Category, string> = {
    RECURRING: t.categoryRecurring,
    DEPOSIT: t.categoryDeposit,
    WITHDRAWAL: t.categoryWithdrawal,
  };
  const CATEGORY_DESCRIPTIONS: Record<Category, string> = {
    RECURRING: t.categoryRecurringDesc,
    DEPOSIT: t.categoryDepositDesc,
    WITHDRAWAL: t.categoryWithdrawalDesc,
  };

  useEffect(() => {
    if (!open) return;
    if (manualFee) {
      const i18n = manualFee.nameI18n || {};
      const nameInUserLang = localize(i18n);
      const otherTranslations = Object.fromEntries(
        Object.entries(i18n).filter(([lang]) => lang !== userLanguage),
      );
      setName(nameInUserLang);
      setNameI18n(otherTranslations);
      setCategory(manualFee.category as Category);
      setValuePct(manualFee.valuePct != null ? String(manualFee.valuePct) : "");
      setMinFee(manualFee.minFeeAmount != null ? String(manualFee.minFeeAmount) : "");
      setClientCostCategoryId(manualFee.clientCostCategoryId || "");
      setProductCostClassId(manualFee.productCostClassId || "");
    } else {
      setName("");
      setNameI18n({});
      setCategory("RECURRING");
      setValuePct("");
      setMinFee("");
      setClientCostCategoryId("");
      setProductCostClassId("");
    }
    setError(null);
  }, [open, manualFee, userLanguage, localize]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t.nameRequired);
      return;
    }

    const pct = Number(valuePct.trim());
    if (!valuePct.trim() || !Number.isFinite(pct) || pct <= 0 || pct > 100) {
      setError(t.invalidPercentage);
      return;
    }

    let minFeeAmount: number | null = null;
    if (supportsMinFee && minFee.trim() !== "") {
      const m = Number(minFee.trim());
      if (!Number.isFinite(m) || m < 0) {
        setError(t.invalidMinFee);
        return;
      }
      minFeeAmount = m;
    }

    const completeNameI18n: LocalizedString = { ...nameI18n, [userLanguage]: name.trim() };

    const submitData = {
      nameI18n: completeNameI18n,
      costType: "pct",
      useBrackets: false,
      category,
      isManual: true,
      valuePct: pct,
      minFeeAmount,
      clientCostCategoryId: clientCostCategoryId || null,
      productCostClassId: productCostClassId || null,
      brackets: [],
      ...(overridesEnabled && { advisorOverride }),
    };

    const callbacks = {
      onSuccess: () => onOpenChange(false),
      onError: (err: Error) => setError(err.message || t.failedToSave),
    };

    if (isEditing) {
      updatePCost.mutate(
        { collectionId: portfolioCollectionId, pcId: manualFee.id, data: submitData },
        callbacks,
      );
    } else {
      createPCost.mutate({ collectionId: portfolioCollectionId, data: submitData }, callbacks);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogScrollContent className="max-w-lg">
        <DialogScrollHeader>
          <DialogTitle>
            {isEditing
              ? editingManualFeeName
                ? `${t.editManualFee} (${editingManualFeeName})`
                : t.editManualFee
              : t.addManualFee}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t.editDescription : t.createDescription}
          </DialogDescription>
        </DialogScrollHeader>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col min-h-0">
          <DialogBody className="space-y-6">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <LocalizedInput
              id="manual-fee-name"
              label={t.name}
              value={name}
              i18nValue={nameI18n}
              onChange={(value) => setName(value)}
              onI18nChange={(i18nValue) => setNameI18n(i18nValue)}
              supportedLanguages={tenantSupportedLanguages}
              placeholder={t.namePlaceholder}
              required
              disabled={isPending}
            />

            <div className="space-y-2">
              <Label htmlFor="manual-fee-category">{t.whenToApply}</Label>
              <Select
                value={category}
                onValueChange={(value) => setCategory(value as Category)}
                disabled={isPending}
              >
                <SelectTrigger id="manual-fee-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(CATEGORY_LABELS) as Category[]).map((value) => (
                    <SelectItem key={value} value={value}>
                      {CATEGORY_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{CATEGORY_DESCRIPTIONS[category]}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="manual-fee-pct">
                {t.percentage} <span className="text-destructive">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="manual-fee-pct"
                  type="number"
                  step="any"
                  min="0"
                  max="100"
                  value={valuePct}
                  onChange={(e) => setValuePct(e.target.value)}
                  placeholder={t.percentagePlaceholder}
                  disabled={isPending}
                  className="w-32"
                  required
                />
                <span className="text-muted-foreground">%</span>
              </div>
            </div>

            {supportsMinFee && (
              <div className="space-y-2">
                <Label htmlFor="manual-fee-min">
                  {t.minFee}{" "}
                  <span className="text-xs font-normal text-muted-foreground">
                    ({t.minFeeOptional})
                  </span>
                </Label>
                <Input
                  id="manual-fee-min"
                  type="number"
                  step="any"
                  min="0"
                  value={minFee}
                  onChange={(e) => setMinFee(e.target.value)}
                  placeholder={t.minFeePlaceholder}
                  disabled={isPending}
                  className="w-32"
                />
                <p className="text-xs text-muted-foreground">{t.minFeeHint}</p>
              </div>
            )}

            {(activeClientCategories.length > 0 || activeProductClasses.length > 0) && (
              <div className="space-y-3 border-t pt-4">
                <div>
                  <Label>{t.mapping}</Label>
                  <p className="text-xs text-muted-foreground">{t.mappingHint}</p>
                </div>

                {activeClientCategories.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="manual-fee-client-cat">{t.clientCategory}</Label>
                    <Select
                      value={clientCostCategoryId || "__any__"}
                      onValueChange={(v) => setClientCostCategoryId(v === "__any__" ? "" : v)}
                      disabled={isPending}
                    >
                      <SelectTrigger id="manual-fee-client-cat">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__any__">{t.any}</SelectItem>
                        {activeClientCategories.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {localize(c.nameI18n)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {activeProductClasses.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="manual-fee-product-class">{t.productClass}</Label>
                    <Select
                      value={productCostClassId || "__any__"}
                      onValueChange={(v) => setProductCostClassId(v === "__any__" ? "" : v)}
                      disabled={isPending}
                    >
                      <SelectTrigger id="manual-fee-product-class">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__any__">{t.any}</SelectItem>
                        {activeProductClasses.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {localize(c.nameI18n)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
            {overridesEnabled && (
              <AdvisorOverrideSelect
                id="manual-fee-advisor-override"
                value={advisorOverride}
                onChange={setAdvisorOverride}
                disabled={isPending}
              />
            )}
          </DialogBody>

          <DialogScrollFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={isPending}>
              {isEditing ? t.save : t.addManualFee}
            </Button>
          </DialogScrollFooter>
        </form>
      </DialogScrollContent>
    </Dialog>
  );
}
